import { useContext, useCallback } from 'react';
import { modalContext } from '@/context/modal.context';
import { toastContext } from '@/context/toast.context';
import Collection from '@/utils/api/odas/collection';
import { isSuccessResponse } from '@/constants/httpStatus';

/**
 * Custom hook to handle adding a new category to a collection
 * @param {Object} options - Hook options
 * @param {Object} options.collection - The collection to add the category to
 * @param {Function} options.updateCategories - Callback to refresh the categories after adding
 * @param {Function} options.t - Translation function
 * @returns {Function} - Event handler for adding a new category
 */
export const useAddCategoryModal = ({ collection, updateCategories, t }) => {
  const { enqueueModal, closeModal } = useContext(modalContext);
  const { addToast } = useContext(toastContext);

  const errorMessage = t('Sorry, something went wrong. Please try again.');

  /**
   * Opens modal to add a new category to the collection
   */
  const addNewCategory = useCallback(
    (e) => {
      e.preventDefault();

      const handleError = () => {
        addToast({ message: errorMessage, type: 'error' });
      };

      enqueueModal({
        name: 'AddNewCategoryModal',
        title: t('Add new category'),
        confirmButtonText: t('Add category'),
        declineButtonText: t('Cancel'),
        buttonOrder: ['decline', 'confirm'],
        onConfirm: (formData) => {
          Collection.addNewCategory(collection, {
            category: { title: formData.categoryName },
            parent_collection_id: collection.id
          })
            .then((resp) => {
              if (isSuccessResponse(resp.status)) {
                updateCategories();
              } else {
                handleError();
              }
            })
            .catch(handleError);
          closeModal();
        },
        onCancel: closeModal,
        onError: () => {
          handleError();
          closeModal();
        },
        resource: collection,
        resourceType: 'collection',
        action: 'add'
      });
    },
    [collection, updateCategories, t, enqueueModal, closeModal, addToast, errorMessage]
  );

  return addNewCategory;
};

export default useAddCategoryModal;
